import { Injectable } from "@angular/core";
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpResponse,
  HttpResponseBase,
} from "@angular/common/http";
import { BehaviorSubject, Observable, Observer, Subscriber } from "rxjs";
import { Messages } from "../model/messages";
import { EnvironmentHelper } from "./env.helper";
import { Router } from "@angular/router";
import { AuthService } from "../auth.service";
import { Media } from "../model/media";
import { DomSanitizer, SafeUrl } from "@angular/platform-browser";

@Injectable({
  providedIn: "root",
})
export class MediaService {
  private apiUrl: string;
  private byIdUrl: string;
  private uploadUrl: string;
  private _currentMedia: BehaviorSubject<Media[]>;
  public selectedMedia: BehaviorSubject<Media | null>;
  private guardDownloadMediaUrl = false;

  constructor(
    private http: HttpClient,
    private env: EnvironmentHelper,
    private router: Router,
    private auth: AuthService,
    private sanitizer: DomSanitizer
  ) {
    this._currentMedia = new BehaviorSubject<Media[]>([]);
    this.selectedMedia = new BehaviorSubject<Media | null>(null);
    this.apiUrl = `${env.apiBaseUrl}media/`;
    this.byIdUrl = `${env.apiBaseUrl}media/by-id/`;
    this.uploadUrl = `${env.apiBaseUrl}media/upload/`;
  }
  public getMediaById(token: string, ticketId: number): Observable<Messages> {
    return new Observable<Messages>((observer) => {
      this.http
        .get<any>(`${this.byIdUrl}${ticketId}`, {
          headers: {
            Authorization: "Bearer " + token,
            "Content-Type": "application/json",
          },
          observe: "response",
        })
        .subscribe(
          (response) => this.receiveMedias(response, observer),
          (error) => this.receiveMedias(error, observer)
        );
    });
  }

  uploadMedia(
    token: string,
    media: Media,
    formData: FormData
  ): Observable<Messages> {
    return new Observable<Messages>((observer) => {
      this.http
        .post<any>(this.uploadUrl, formData, {
          headers: {
            Authorization: "Bearer " + token,
          },
          observe: "response",
        })
        .subscribe(
          (response) => this.receiveMedia(response, observer),
          (error) => this.receiveMedia(error, observer)
        );
    });
  }

  public createMedia(
    token: string,
    ticketId: number,
    media: Media
  ): Observable<Messages> {
    return new Observable<Messages>((observer) => {
      this.http
        .post<any>(this.apiUrl, media, {
          headers: {
            Authorization: "Bearer " + token,
            "Content-Type": "application/json",
          },
          observe: "response",
        })
        .subscribe(
          (response) => this.receiveMedia(response, observer),
          (error) => this.receiveMedia(error, observer)
        );
    });
  }

  public updateMedia(
    token: string,
    ticketId: number,
    media: Media
  ): Observable<Messages> {
    const url = `${this.apiUrl}by-id/${media.id}`;
    return new Observable<Messages>((observer) => {
      if (!media.id) {
        observer.next(Messages.from({ media: ["media.id.required"] }));
        return;
      }

      this.http
        .put<any>(url, media, {
          headers: {
            Authorization: "Bearer " + token,
            "Content-Type": "application/json",
          },
          observe: "response",
        })
        .subscribe(
          (response) => this.receiveMedia(response, observer),
          (error) => this.receiveMedia(error, observer)
        );
    });
  }

  public deleteMedia(
    token: string,
    ticketId: number,
    mediaId: number
  ): Observable<Messages> {
    const url = `${this.apiUrl}by-id/${mediaId}`;
    return new Observable<Messages>((observer) => {
      this.http
        .delete<any>(url, {
          headers: {
            Authorization: "Bearer " + token,
            "Content-Type": "application/json",
          },
          observe: "response",
        })
        .subscribe(
          (response) => this.receiveMedia(response, observer),
          (error) => this.receiveMedia(error, observer)
        );
    });
  }

  private receiveMedias(
    response: HttpResponseBase,
    observer: Subscriber<Messages>
  ) {
    if (response.status === 200) {
      const data = (response as HttpResponse<Media[]>)?.body;
      if (!data) {
        observer.next(Messages.from({ media: ["media.unknown"] }));
        return;
      }
      this._currentMedia.next(data);
      observer.next(Messages.EMPTY);
    } else if (response.status === 401) {
      observer.next(
        Messages.orElse(
          (response as HttpErrorResponse).error,
          "auth",
          "unauthorized.access."
        )
      );
    } else {
      observer.next(
        Messages.orElse(
          (response as HttpErrorResponse).error,
          "media",
          "media.load.error"
        )
      );
    }
  }

  private receiveMedia(
    response: HttpResponseBase,
    observer: Subscriber<Messages>
  ) {
    if (response.status === 200) {
      const data = (response as HttpResponse<Media[]>)?.body;
      if (!data) {
        observer.next(Messages.from({ media: ["media.unknown"] }));
        return;
      }
      this._currentMedia.next(data);
      observer.next(Messages.EMPTY);
    } else if (
      response.status === 400 ||
      response.status === 401 ||
      response.status === 409 ||
      response.status === 500
    ) {
      observer.next(
        Messages.orElse(
          (response as HttpErrorResponse).error,
          "media",
          "media.load.error"
        )
      );
    } else {
      observer.next(
        Messages.orElse(
          (response as HttpErrorResponse).error,
          "general",
          "an.unexpected.error.has.occurred."
        )
      );
    }
  }

  downloadAuthenticatedMediaUrl(
    url: string,
    token: string,
    imageUrl: Observer<SafeUrl>
  ): Observable<Messages> {
    return new Observable<Messages>((observer) => {
      if (this.guardDownloadMediaUrl) {
        observer.next(Messages.EMPTY);

        return;
      }
      if (!url) {
        observer.next(Messages.from({ media: ["media.url.required"] }));
        return;
      }

      this.guardDownloadMediaUrl = true;
      this.http
        .get(url, {
          responseType: "blob",
          headers: { Authorization: `Bearer ${token}` },
        })
        .subscribe({
          next: (blob) => {
            const blobUrl = window.URL.createObjectURL(blob);
            const sanitizedUrl = this.sanitizer.bypassSecurityTrustUrl(blobUrl);
            imageUrl.next(sanitizedUrl);
            observer.next(Messages.EMPTY);
            this.guardDownloadMediaUrl = false;
          },
          error: (err) => {
            observer.next(
              Messages.from({ media: ["media.upload.error", err] })
            );
            this.guardDownloadMediaUrl = false;
          },
        });
    });
  }

  public selectFromRouter() {
    const segments = this.getLastSuccessfulNavigationSegments();

    if (segments && segments.length > 0) {
      const mediaId = segments[segments.length - 1];
      const token = this.auth.token;

      this.getMediaById(token, Number(mediaId)).subscribe();
    } else {
      const emptyMedia = new Media(
        0,
        "",
        0,
        "",
        0,
        false,
        null,
        null,
        null,
        null
      );
      this.select(emptyMedia);
    }
  }
  private getLastSuccessfulNavigationSegments(): string[] | null {
    const segments =
      this.router.lastSuccessfulNavigation?.finalUrl?.root.children["primary"]
        .segments;
    return segments ? segments.map((segment) => segment.path) : null;
  }

  public select(media: Media) {
    this.selectedMedia.next(media);
  }

  public accept(media: Media[]) {
    this._currentMedia.next(media);
  }

  public get selectedMediaObservable(): Observable<Media | null> {
    return this.selectedMedia.asObservable();
  }
  public get mediaObservable(): Observable<Media[]> {
    return this._currentMedia.asObservable();
  }
  public get mediaData(): Media[] {
    return this._currentMedia.value;
  }

  public get currentMedia(): Media {
    const value = this.selectedMedia.value;
    return value || Media.EMPTY;
  }
  async downloadAuthenticatedMediaUrlAsSafeUrl(
    url: string,
    token: string
  ): Promise<SafeUrl> {
    const blob = (await this.http
      .get(url, {
        responseType: "blob",
        headers: { Authorization: `Bearer ${token}` },
      })
      .toPromise()) as Blob;

    const blobUrl = window.URL.createObjectURL(blob);
    const safeUrl = this.sanitizer.bypassSecurityTrustUrl(blobUrl);

    return safeUrl;
  }
  public getMediaByIdRaw(token: string, mediaId: number): Observable<Media> {
    return this.http.get<Media>(`${this.byIdUrl}${mediaId}`, {
      headers: {
        Authorization: "Bearer " + token,
        "Content-Type": "application/json",
      },
    });
  }
}
